import React, { ReactText, useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Grid, Button } from "@material-ui/core";

import { useForm } from "../../../hooks/useForm";
import { useShowBlock } from "../../../hooks/useShowBlock";
import { useFindWagerQuery } from "../../../api/wager";
import { useHallConfigQuery } from "../../../api/payout";
import { packageSelector } from "../../../redux/packageReducer";
import { formIsError, hallSelector } from "../../../redux/hallReducer";
import { authSelector } from "../../../redux/authReducer";
import { getDefaultHallWagerData, hallWagerToHallWagerData } from "../../../services/wager";
import { findPermissionOr } from "../../../utils/find";
import { prettyNum } from "../../../utils/prettyNum";
import { defaultFace } from "../../../settings/hall";

import { FormWrapper, useStyles } from "./styles";
import CurrencyForm from "./Currency";
import CityForm from "./City";
import CompanyForm from "./Company";
import Overlay from "../../Common/Overlay/index";
import Preloader from "../../Common/Preloader";
import ErrorText from "../../Common/ErrorText";
import JackpotsList from "../../Jackpots/List";
import TablesTab from "../View/Tables";
import Binding from "../View/Binding";
import Info from "./tabs/info";
import Hotkeys from "../../Hotkeys";
import MathTab from "./tabs/math";
import GamePackages from "../View/game-packages";
import CardsList from "../../Cards/List";
import WagersForm from "../../Wagers/Form";
import Packets from "./tabs/packets";
import TabTitles from "../tab-titles";
import PaymentsConfig from "./tabs/payments-config";

import { IGame, HallData, IHallMathForm, HallWagerData, GamesType } from "../../../interfaces";
import { HallFormProps, PaymentListItemHallFormItem } from "./interface";

const HallForm: React.FC<HallFormProps> = ({
    onSubmit, cancelBtnEvent, cancelBtnText, submitBtnText, user_id, id, name, address,
    city_id, limit_id, currency_id, company_id, owner_id, cashback, out_percent, poll_inc,
    packets, denomination, denominations, status,
    max_win_from_deposit = 100, max_win_per_spin = 10000, win_per_spin_percent_bet = 100,
    win_from_deposit_for_lose = 100, max_deposit_for_lose = 10000, min_cashback_deposit = 0,
    math_type = 4, hall_template = defaultFace, is_social_poll = 1, show_logo = 1, text = "",
    is_school, tab, setTab, change, hideForm, keyboard_type = 0, show_currency = 0,
    language = "RUS", jackpot_migration = 0, math, wheel_recharge = false, cashback_from_hall = false,
    reel_animation = 0, oferta: ofertaInit = 0, cashback_reel = 80, registration_code = "",
    payment_bonus_from_hall = true, cards = 0, cashback_type = 0, default_wager_percent = 100, wheel_recharge_zero = false,
}) => {
    const dispatch = useDispatch();
    const classes = useStyles();

    const { packages: { items: packages } } = useSelector(packageSelector);
    const { data: hall, form: { error, loading: formLoading } } = useSelector(hallSelector);
    const { assigments: { items }, role: { item: userRole } } = useSelector(authSelector);

    const { data: wager } = useFindWagerQuery(id!, { skip: !id });
    const [wagerForm, setWagerForm] = useState<HallWagerData | null>(wager ? hallWagerToHallWagerData(wager) : (id ? null : getDefaultHallWagerData()));

    useEffect(() => {
        if (!wagerForm && wager) {
            setWagerForm(hallWagerToHallWagerData(wager));
        }
    }, [wager, wagerForm]);

    const isAdmin = useMemo(() => userRole === "administrator", [userRole]);

    const { data: defaultPaymentListItems } = useHallConfigQuery(id!, { skip: !isAdmin || !id });
    const [paymentListItems, setPaymentListItems] = useState<PaymentListItemHallFormItem[] | null>(null);

    useEffect(() => {
        if (!paymentListItems && defaultPaymentListItems) {
            setPaymentListItems(defaultPaymentListItems.data.map(item => ({ ...item })));
        }
    }, [defaultPaymentListItems, paymentListItems]);

    const defaultForm = useMemo(() => ({
        name: name,
        address: address ? address : "",
        status: status,
        denom: "",
        max_win_from_deposit: max_win_from_deposit ? max_win_from_deposit : 100,
        max_win_per_spin: prettyNum(max_win_per_spin) ? prettyNum(max_win_per_spin) : prettyNum(10000),
        win_per_spin_percent_bet: win_per_spin_percent_bet ? win_per_spin_percent_bet : 100,
        win_from_deposit_for_lose: win_from_deposit_for_lose ? win_from_deposit_for_lose : 100,
        max_deposit_for_lose: prettyNum(max_deposit_for_lose) ? prettyNum(max_deposit_for_lose) : prettyNum(10000),
        min_cashback_deposit: min_cashback_deposit ? min_cashback_deposit / 100 : 0,
        math_type, hall_template, is_social_poll, show_logo, text, is_school, keyboard_type, show_currency,
        language, jackpot_migration, math, reel_animation, oferta: ofertaInit, cashback_reel, wheel_recharge,
        cashback_from_hall, cashback_type, registration_code, cards, payment_bonus_from_hall, default_wager_percent, wheel_recharge_zero,
    }), [
        address, cards, cashback_from_hall, cashback_type, cashback_reel, hall_template, is_school, is_social_poll,
        jackpot_migration, keyboard_type, language, math, math_type, max_deposit_for_lose, max_win_from_deposit,
        max_win_per_spin, min_cashback_deposit, name, ofertaInit, reel_animation, registration_code, show_currency,
        show_logo, status, text, wheel_recharge, wheel_recharge_zero, win_from_deposit_for_lose, win_per_spin_percent_bet,
        payment_bonus_from_hall, default_wager_percent
    ]);

    const { form, handleInputChange, clearForm, setFormItem } = useForm(defaultForm);

    const [user, setUser] = useState<number | undefined>(user_id ? user_id : owner_id);
    const [city, setCity] = useState<number | undefined>(city_id);
    const [limit, setLimit] = useState<number | null | undefined>(limit_id);
    const [currency, setCurrency] = useState<string | undefined>(currency_id);
    const [company, setCompany] = useState<number | undefined>(company_id);

    const [denominationsSelected, setDenominationsSelected] = useState<ReactText[]>(denominations ?? [100]);
    const [defaultDenomination, setDefaultDenomination] = useState<number>(denomination || 100);
    const [selectedCashback, setSelectedCashback] = useState<number>(cashback || 0);
    const [outPercent, setOutPercent] = useState<number>(out_percent || 85);
    const [pollInc, setPollInc] = useState<number>(poll_inc || 10);
    const [oferta, setOferta] = useState<number>(ofertaInit);
    const [isInherited, setIsInherited] = useState<boolean>(!packets || packets.length === 0);

    const getGamesId = useCallback((games: IGame[]) => {
        return games.map(item => item.id);
    }, []);

    const [packagesSelected, setPackagesSelected] = useState<number[]>(() => {
        if (!packets) return [];
        return packets.map(item => item.package.id);
    });

    const [gamesSelected, setGamesSelected] = useState<GamesType[]>(() => {
        if (!packets) return [];
        return packets.map(item => {
            const gamesIds = getGamesId(item.games);
            return {
                package_id: item.package.id,
                mode: gamesIds.length === 0 ? "ALL_PROVIDER" : "CUSTOM",
                games: gamesIds,
            };
        });
    });

    const getPackagesData = useCallback(() => {
        // Если включено глобальное наследование (isInherited === true)
        if (isInherited) {
            return { packages: [] };
        }

        // Если ручная настройка пакетов
        const data = packagesSelected.map((packageId: number) => {
            const selection = gamesSelected.find((i: GamesType) => i.package_id === packageId);

            return {
                package: { id: packageId },
                // Если ALL_PROVIDER, принудительно отправляем пустой массив
                games: selection?.mode === "ALL_PROVIDER" ? [] : (selection?.games || []),
            };
        });

        return { packages: data };
    }, [isInherited, packagesSelected, gamesSelected]);

    const data: HallData = useMemo(() => ({
        id: 0,
        name: form.name,
        company_id: company,
        city_id: city,
        limit_id: limit,
        currency_id: currency,
        cashback: selectedCashback,
        address: form.address,
        out_percent: outPercent,
        poll_inc: pollInc,
        bet_list: "",
        bet_max: 1,
        bet_min: 1,
        status: form.status ? 10 : 0,
        owner_id: user,
        denomination: defaultDenomination,
        denominations: denominationsSelected,
        max_win_from_deposit: form.max_win_from_deposit,
        max_win_per_spin: form.max_win_per_spin * 100,
        win_per_spin_percent_bet: form.win_per_spin_percent_bet,
        win_from_deposit_for_lose: form.win_from_deposit_for_lose,
        max_deposit_for_lose: form.max_deposit_for_lose * 100,
        min_cashback_deposit: form.min_cashback_deposit * 100,
        math_type: form.math_type,
        hall_template: form.hall_template,
        is_social_poll: form.is_social_poll,
        show_logo: form.show_logo,
        text: form.text,
        is_school: form.is_school,
        keyboard_type: form.keyboard_type || 0,
        show_currency: form.show_currency || 0,
        language: form.language || "RUS",
        jackpot_migration: form.jackpot_migration || 0,
        math: form.math,
        reel_animation: form.reel_animation,
        oferta: form.oferta,
        cashback_reel: form.cashback_reel,
        registration_code: form.registration_code,
        cards: form.cards,
        wheel_recharge: !!+form.wheel_recharge,
        wheel_recharge_zero: !!+form.wheel_recharge_zero,
        cashback_from_hall: !!+form.cashback_from_hall,
        cashback_type: form.cashback_type,
        payment_bonus_from_hall: !!+form.payment_bonus_from_hall,
        default_wager_percent: form.default_wager_percent || 100,
    }), [city, company, currency, defaultDenomination, denominationsSelected, form, limit, outPercent, pollInc, selectedCashback, user]);

    const initialMath = form.math || { payout: "90", max_rtp: "92", min_rtp: "82", adaptive_type: 0, adaptive_param: 0, overdraft: "0", reels_path: "reels" };
    const [hallMathForm, setHallMathForm] = useState<IHallMathForm>({ ...initialMath });

    const { showBlock: showCity, isBlockShown: isCity, hideBlock: hideCity } = useShowBlock();
    const { showBlock: showCurrency, isBlockShown: isCurrency, hideBlock: hideCurrency } = useShowBlock();
    const { showBlock: showCompany, isBlockShown: isCompany, hideBlock: hideCompany } = useShowBlock();

    useEffect(() => {
        return () => {
            clearForm();
            dispatch(formIsError(null));
        };
    }, [dispatch, clearForm]);

    // Глобальный слушатель только для закрытия формы по Escape
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.code === "Escape") {
                hideForm();
            }
        };
        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [hideForm]);

    const handleFormSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSubmit(data, getPackagesData(), hallMathForm, hideForm, wagerForm ?? undefined, paymentListItems ?? undefined, id);
    };

    const validation = form.max_win_from_deposit < 100 || form.max_win_per_spin < 100 || form.max_deposit_for_lose < 100 || form.win_from_deposit_for_lose < 100 || form.win_per_spin_percent_bet < 100;

    const returnTab = (tab_id: number) => {
        switch (tab_id) {
            case 0:
                return (
                    <Info
                        form={form} handleInputChange={handleInputChange} error={error} change={change || false} setFormItem={setFormItem}
                        showCity={showCity} showCurrency={showCurrency} showCompany={showCompany} user_id={user_id || 0} setUser={setUser} setCompany={setCompany}
                        ownerId={user || owner_id || 0} cityId={data.city_id || 0} setCity={setCity} limitId={data.limit_id || 0} setLimit={setLimit}
                        currency={data.currency_id || ""} setCurrency={setCurrency} companyId={data.company_id || 0}
                        outPercent={data.out_percent!} setOutPercent={setOutPercent} pollInc={data.poll_inc!} setPollInc={setPollInc}
                        selectedCashback={data.cashback!} setSelectedCashback={setSelectedCashback}
                        denominations={data.denominations!} setDenominationsSelected={setDenominationsSelected}
                        denomination={data.denomination || 100} setDefaultDenomination={setDefaultDenomination} oferta={oferta} setOferta={setOferta}
                    />
                );
            case 1:
                return findPermissionOr(items, ["updateHallPackage", "hallChildrenPackage"])
                    ? <Packets
                        isInherited={isInherited}
                        setIsInherited={setIsInherited}
                        packagesSelected={packagesSelected}
                        setPackagesSelected={setPackagesSelected}
                        gamesSelected={gamesSelected}
                        setGamesSelected={setGamesSelected}
                        getGamesId={getGamesId}
                    />
                    : <GamePackages packages={packages.filter(p => packagesSelected.includes(p.id))} />;
            case 2:
                return id && <TablesTab hallId={id} change />;
            case 3:
                return <JackpotsList hall_id={id} change />;
            case 4:
                return id && <Binding hall_id={id} change />;
            case 6:
                return id && findPermissionOr(items, ["updateHallMath", "updateChildrenHallMath"]) && <MathTab hallMathForm={hallMathForm} setHallMathForm={setHallMathForm} />;
            case 7:
                return id && <Hotkeys hallId={id} />;
            case 9:
                return !!id && !!cards && <CardsList hall_id={id} change />;
            case 12:
                return !!id && !!wagerForm && <WagersForm form={wagerForm} setForm={setWagerForm} />;
            case 13:
                return !!id && paymentListItems && <PaymentsConfig hall_id={id} paymentListItems={paymentListItems} setPaymentListItems={setPaymentListItems} />;
            default:
                setTab(4);
                return null;
        }
    };

    if (formLoading || (id && !wagerForm)) {
        return <Preloader absolute={false} />;
    }

    return (
        <FormWrapper onSubmit={handleFormSubmit}>
            <TabTitles setTab={setTab} currentTab={tab} hall={hall ?? undefined} isChange={true} />

            <Grid container direction="row" justifyContent="center" alignItems="flex-start" spacing={2}>
                {returnTab(tab)}
                <Grid item xs={12}>
                    <Box display="flex" justifyContent="center" alignItems="center">
                        <Button fullWidth className={classes.submitBtn} variant="contained" onClick={cancelBtnEvent}>
                            {cancelBtnText}
                        </Button>
                        <Button fullWidth disabled={validation} className={classes.submitBtn} variant="contained" type="submit">
                            {submitBtnText}
                        </Button>
                    </Box>
                </Grid>
                <Grid item xs={12}>
                    <ErrorText error={error} />
                </Grid>
            </Grid>

            {isCity && (
                <Overlay>
                    <CityForm hideCity={hideCity} />
                </Overlay>
            )}
            {isCompany && (
                <Overlay>
                    <CompanyForm hideCompany={hideCompany} />
                </Overlay>
            )}
            {isCurrency && (
                <Overlay>
                    <CurrencyForm hideCurrency={hideCurrency} />
                </Overlay>
            )}
        </FormWrapper>
    );
};

export default HallForm;